import React, {useEffect, useState} from 'react';
import {Modal, Button, Card, Icon} from '@salesforce/design-system-react';
import ChangeCaseInput from "../../../../utils/components/input/changeCaseInput";
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";
import {SCPDeploymentAPIServiceFactory} from "../../../../services/deployment/scp/factory";
import {GenericDeploymentSubmissionResponse} from "../../../../utils/types/deployments/genericDeploymentResponse";
import {ChangeCaseMetadata} from "../../../../utils/types/gus";
import ImplementationStepSelector from "../../../../utils/components/input/changeCaseImplementationSteps";

interface RollbackOperationModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedOperationID: string;
    selectedCommitID: string;
    selectedCommitTitle: string;
    selectedCommitDeveloper: string;
    onExpandSuccess: (apiResponse: GenericDeploymentSubmissionResponse) => void;
}

const SCPRollbackOperationModal: React.FC<RollbackOperationModalProps> = ({ isOpen, onClose, onExpandSuccess, selectedOperationID, selectedCommitID, selectedCommitTitle, selectedCommitDeveloper }) => {
    const [changeCaseID, setChangeCaseID] = useState<string>('');
    const [isValidChangeCase, setIsValidChangeCase] = useState<boolean>(false);
    const [isRollbackInProgress, setIsRollbackInProgress] = useState<boolean>(false);
    const [disableRollbackButton, setDisableRollbackButton] = useState<boolean>(false);
    const [rollbackAPIResponse, setRollbackAPIResponse] = useState<GenericDeploymentSubmissionResponse | undefined>(undefined);
    const [selectedImplStepID, setSelectedImplStepID] = useState<string>('');
    const [ccMetadata, setCCMetadata] = useState<ChangeCaseMetadata | null>(null);

    const scpAPIService = SCPDeploymentAPIServiceFactory.getService();

    useEffect(() => {
        setDisableRollbackButton(isRollbackInProgress || !isValidChangeCase || !isValidImplementationStep())
    }, [isValidChangeCase, isRollbackInProgress, selectedImplStepID]);

    const disableImplStep = () => {
        return isRollbackInProgress || !isValidChangeCase
    }
    const resetImplStep = () => {
        return !isValidChangeCase
    }
    const isValidImplementationStep = () => {
        return selectedImplStepID.length > 0
    }


    const handleRollback = async () => {
        try {
            setIsRollbackInProgress(true);
            const expandResponse = await scpAPIService.rollbackOperation(changeCaseID, selectedOperationID, selectedImplStepID);
            expandResponse.success && onExpandSuccess(expandResponse)
            setRollbackAPIResponse(expandResponse)
        } catch (error) {
            console.log("Error submitting scp operation for Rollback", error);
            setRollbackAPIResponse(new GenericDeploymentSubmissionResponse(`${error}`, false))
        } finally {
            setIsRollbackInProgress(false);
        }
    };

    const getBottomContent = () => {
        if (isRollbackInProgress) {
            return <>
                <Card hasNoHeader={true}>
                    <div style={{minHeight: "25vh"}}>
                        <div className="slds-m-top_xx-large slds-flex slds-flex_column slds-align-items_center">
                            <CustomSpinnerOverlay size={"medium"}/>
                            <p className="slds-m-top_large slds-align_absolute-center slds-text-color--inverse-weak">
                                Submitting operation for Rollback
                            </p>
                        </div>
                    </div>
                </Card>
            </>
        }

        if (rollbackAPIResponse) {
            return <>
                <Card hasNoHeader={true}>
                    {rollbackAPIResponse.success ?
                        <div className="slds-m-around_medium slds-p-around_medium slds-box" style={{minHeight: "25vh"}}>
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_success">
                                Rollback Request Submitted Successfully!
                            </h2>
                        </div> :
                        <div className="slds-m-around_medium slds-p-around_medium slds-box" style={{minHeight: "25vh"}}>
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_destructive">
                                {rollbackAPIResponse.message}
                            </h2>
                        </div>
                    }
                </Card>
            </>
        }

        return <>
            <Card hasNoHeader={true}>
                <div className="slds-p-around_xx-large slds-m-around--xx-large"></div>
            </Card>
        </>
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            size="medium"
            dismissOnClickOutside={false}
            footer={
                <>
                    <Button label="Submit for Rollback" onClick={handleRollback} variant="brand" disabled={disableRollbackButton}/>
                </>
            }
        >
            <Card
                heading={<>
                    <strong className="slds-text-color_error"> Revert Commit: </strong>
                    <strong>{selectedCommitID.length > 0 ? selectedCommitID : "Unknown Commit"}</strong>
                </>}
                icon={<Icon category="standard" name="actions_and_buttons" size="small"/>}
                className="slds-p-vertical_small slds-color__background_gray-3"
                bodyClassName={"slds-p-right_medium slds-p-left_medium"}
            >
                <p> {selectedCommitTitle || "No title provided"}</p>
            </Card>

            <div className="slds-grid slds-wrap">
                {selectedCommitDeveloper.length > 0 && <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Developer: {selectedCommitDeveloper}
                </span>}
                <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Operation ID: {selectedOperationID}
                </span>
            </div>

            <div className="slds-p-around_medium">
                <div className={"slds-box"}>
                    <div className="slds-grid slds-gutters slds-m-around_small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Change Case:</strong></label>
                        </div>
                        <div className="slds-col">
                            <ChangeCaseInput
                                isValidChangeCase={isValidChangeCase}
                                disableInput={isRollbackInProgress}
                                changeCaseID={changeCaseID}
                                setChangeCaseID={setChangeCaseID}
                                setIsValidChangeCase={setIsValidChangeCase}
                                deploymentType={"SCPPolicy"}
                                setCCMetadata={setCCMetadata}
                            ></ChangeCaseInput>
                        </div>
                    </div>

                    {/* custom min height maintains the visibility of dropdown options*/}
                    <div className="slds-grid slds-gutters slds-m-horizontal--small slds-m-top--small">
                        <div className="slds-col slds-size_1-of-5">
                            <label className="slds-form-element__label"><strong>Implementation Step:</strong></label>
                        </div>
                        <div className="slds-size_2-of-3">
                            <div className="slds-grid slds-grid_vertical-align-center">
                                <div className="slds-col">
                                    <ImplementationStepSelector
                                        changeCaseMetadata={isValidChangeCase ? ccMetadata : null}
                                        setSelectedStep={setSelectedImplStepID}
                                        disable={disableImplStep()}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {getBottomContent()}
            </div>
        </Modal>
    );
};

export default SCPRollbackOperationModal;
